/**
 * Level of sensitivity of the log event data.
 *
 * - AUDIT type of event can e.g. contain personal information or
 *    bank account numbers or be significant because of having legal
 *    audit trail related data.
 * - TECH type of event contains purely anonymous information and
 *    has no data covered by regulations or legislation.
 *
 * {@link https://oppconfluence.eficode.com/display/KLHAM/OP+cloud+application+logging+header+structure |OP Cloud Application Logging Header Structure}
 */
export enum LoggerSensitivityType {
  Audit = 'AUDIT',
  Tech = 'TECH',
}

export enum LoggingSeverity {
  Debug = 'DEBUG',
  Info = 'INFO',
  Warn = 'WARN',
  Error = 'ERROR',
}

export enum LoggingEnvironment {
  Dev = 'DEVELOPMENT',
  Test = 'TEST',
  Staging = 'STAGING',
  Prod = 'PRODUCTION',
}

export type ApplicationLogData = Record<string, any> & { text?: never };

type OpHheaderStructureVersion = 2;

/**
 * Logging event structure.
 *
 * {@link https://oppconfluence.eficode.com/display/KLHAM/OP+cloud+application+logging+header+structure |OP Cloud Application Logging Header Structure}
 */
export type ApplicationLogEvent = {
  /**
   * Version info for the header structure.
   */
  opVersion: OpHheaderStructureVersion;
  /**
   * When the activity was performed in RFC 3339 format.
   *
   * {@link https://www.ietf.org/rfc/rfc3339.txt RFC3339}
   */
  timestamp: string;
  application: string;
  environment: LoggingEnvironment;
  component: string;
  sensitivity: LoggerSensitivityType.Tech;
  requestId: string;
  sessionId?: string;
  severity: LoggingSeverity;
  /**
   * Name of the logger which generated the logging event. Usually package,
   * class and method name where the logging message was asked to be logged.
   *
   * On OP-koti this is resolved from call stack to get originating file and line.
   */
  logger: string;
  /**
   * Thread id may not be very useful in Node.js environment especially when
   * containarized. But, if containers first process is not node then this will
   * reveal it.
   */
  thread: string;
  /**
   * Message payload defined by SW Designer = User defined relevant information
   * logged for the particular application in question.
   *
   * Message payload can be another JSON object or logged fields separated with
   * space mark.
   *
   * In OP-Koti message payload is defined as ApplicationLogData + additional log message
   * in "text" property.
   */
  message: Omit<ApplicationLogData, 'text'> & { text?: string };
};

export interface LoggerFunction {
  (text: string | ApplicationLogData): void;
  (text: string, data: ApplicationLogData): void;

  (text: string, error: unknown): void;

  (error: unknown): void;
}

export interface LoggerInstance {
  debug: LoggerFunction;
  info: LoggerFunction;
  warn: LoggerFunction;
  error: LoggerFunction;
}

/**
 * Dynamic properties allows adding keys which are
 * not allowed to override through dynamic properties.
 *
 * This solution should work without any problems with Node.js AsyncLocalStorage
 * and with 'express-http-context'.
 */
type DynamicLogEventPropertiesFunctionResponse = Record<string, string | boolean> & {
  opVersion?: never;
  timestamp?: never;
  application?: never;
  environment?: never;
  component?: never;
  sensitivity?: never;
  severity?: never;
  logger?: never;
  thread?: never;
  message?: never;
};

export interface DynamicLogEventPropertiesFunction {
  (): DynamicLogEventPropertiesFunctionResponse;
}

/**
 * Configuration to create new application logger.
 */
export type CreateLoggerConfig = {
  /**
   * Application name.
   */
  applicationName: string;
  /**
   * Environment where application is deployed.
   */
  environment: LoggingEnvironment;
  /**
   * Logging severity level. Indicates minimum level which are appended into logs.
   *
   * @default INFO
   */
  loggingSeverity?: LoggingSeverity;
  /**
   * Hostname of the machine where application is running.
   *
   * @default os.hostname()
   */
  hostname?: string;
  /**
   * Enable or disable logger appenders.
   *
   * @default true
   */
  loggingEnabled?: boolean;
  /**
   * Enable or disable caller resolution through call stack.
   * Value is placed on 'logger' attribute in log event.
   *
   * @default true
   */
  resolveCaller?: boolean;
  /**
   * Function that allows to inject dynamic properties into log event.
   * This is useful with requestId when this can be resolved from async context.
   *
   * @default noop
   */
  dynamicProperties?: DynamicLogEventPropertiesFunction;
};

export type CreateBrowserLoggerConfig = Omit<CreateLoggerConfig, 'hostname'>;

export type CreateApplicationLogger = (config: CreateLoggerConfig) => LoggerInstance;

export type CreateApplicationLoggerForBrowser = (config: CreateBrowserLoggerConfig) => LoggerInstance;
